// CourseDashboard — landing sub-route under /course/:id.
//
// Decision history:
//   Session 19G (§5.4): stat cards + module nav cards.
//   Session 19I (§I3): icons added per陛下 "页面略单调" feedback.
//   Session 19L (§L): v1-style content breakdown — chapter/topic view switch +
//     per-chapter cards with mastery + cognitive level distribution + weakest
//     top-3 lists. "我们做这么多scheme也就是为了这个 [stats]" — 陛下原话。
//   Session 46: display components + configs extracted to
//     components/dashboard/dashboard-cards.tsx.

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Layers,
  ListChecks,
  AlertTriangle,
  Bookmark,
  Code2,
  TrendingUp,
  Sparkles,
  BookOpen,
  Flag,
  Compass,
} from 'lucide-react';
import { useT, useLocale } from '../i18n/index.js';
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { getEnabledStages } from '../lib/regen-utils.js';
import { useEventBus } from '../lib/hooks/use-event-bus.js';
import { useDashboardStats } from '../lib/hooks/use-dashboard-stats.js';
import { wrongAnswerStats } from '../lib/stats.js';
import { updateProgress } from '../storage/idb.js';
import { StateCard } from '../components/state-card.js';
import { DiagnoseDialog } from '../components/diagnose-dialog.js';
import { DiagnoseVerdictCard } from '../components/cards/diagnose-verdict-card.js';
import type { DiagnoseVerdict } from '@examforge/shared/schemas';
import {
  StatCard,
  ModuleCard,
  ChapterCard,
  TopicCard,
  CognitiveBar,
  masteryClass,
  pickLabel,
} from '../components/dashboard/dashboard-cards.js';
import { ChapterCognitiveHeatmap } from '../components/dashboard/chapter-cognitive-heatmap.js';

type ViewMode = 'chapter' | 'topic';

export function CourseDashboard() {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { draft, pkg, progress, refreshProgress } = useStoreCourseData();
  const eventBus = useEventBus();
  const [viewMode, setViewMode] = useState<ViewMode>('chapter');

  // Isomorphism Phase 3: subscribe to Event Bus for live dashboard updates.
  // When quiz/flashcard events fire, refresh progress from IDB so stats
  // stay current without full page reload.
  useEffect(() => {
    const unsub1 = eventBus.on('quiz:completed', () => { void refreshProgress(); });
    const unsub2 = eventBus.on('flashcard:reviewed', () => { void refreshProgress(); });
    return () => { unsub1(); unsub2(); };
  }, [eventBus, refreshProgress]);

  // 19AQ · F2 → 19AR · G1 correction: per-card settings/delete moved to home
  // page course cards (陛下 "之前说的是 home 页的 course card 而非 chapter card").
  // ChapterCard stays display-only for settings/delete, but 19BJ Session 5 adds
  // a chapter-scoped content-regen button (regen ≠ settings/delete — it
  // regenerates a stage's content and surfaces RegenPreview inline).
  void navigate;

  const {
    enabled, dueCount, unresolvedCount, bookmarkCount, flaggedCount,
    chStats, tpStats, cogDist, cogMatrix, weakChapters, weakTopics, totalCogLabeled,
  } = useDashboardStats(draft, pkg, progress);

  // 19BO+ Line 2 B4.3: AI 弱项诊断接入。数据源 = wrongAnswerStats（与 DiagnoseRequest
  // 1:1 对齐）。diagStats 只在 pkg/progress 变化时重算。诊断是课程级快照，持久化到
  // progress.diagnosis（随 syncCourse opaque payload 同步）。
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);
  const diagStats = useMemo(() => wrongAnswerStats(pkg, progress), [pkg, progress]);

  async function handleDiagnosed(verdict: DiagnoseVerdict): Promise<void> {
    await updateProgress(draft.id, (p) => {
      p.diagnosis = {
        verdict,
        statsSnapshot: { total: diagStats.total, unresolved: diagStats.unresolved, resolved: diagStats.resolved },
      };
    });
    await refreshProgress();
  }

  // B5 桥5: 后台自动诊断产出的 verdict 进 progress.diagnosisPending 待审队列。
  // accept → 提升为当前生效 diagnosis（用 pending 自带的 statsSnapshot，因为
  //   诊断基于的是产出那一刻的错题统计，不是现在的）+ 清空 pending。
  // reject → 直接清空 pending（verdict 丢弃）。
  // 两者都 refreshProgress 让 UI 立即反映。
  async function handleAcceptPending(): Promise<void> {
    const pending = progress.diagnosisPending;
    if (!pending) return;
    await updateProgress(draft.id, (p) => {
      p.diagnosis = {
        verdict: pending.verdict,
        statsSnapshot: pending.statsSnapshot,
      };
      p.diagnosisPending = null;
    });
    await refreshProgress();
  }

  async function handleRejectPending(): Promise<void> {
    await updateProgress(draft.id, (p) => {
      p.diagnosisPending = null;
    });
    await refreshProgress();
  }

  // 19BJ Session 5: stages available for chapter-scoped regen on this course.
  const enabledStages = getEnabledStages(draft.enabledComponents);

  return (
    <section className="dashboard">
      {/* ── Top stat cards ─────────────────────────────────────────────── */}
      <div className="dashboard-stats">
        {enabled.has('flashcards') && (
          <StatCard
            icon={<Layers size={20} aria-hidden="true" />}
            tone="primary"
            value={dueCount}
            label={t('dashboard.flashcardsDue')}
            sub={t('dashboard.flashcardsTotal', { count: pkg.flashcards.length })}
          />
        )}
        {enabled.has('wrong_answers') && (
          <StatCard
            icon={<AlertTriangle size={20} aria-hidden="true" />}
            tone="warning"
            value={unresolvedCount}
            label={t('dashboard.wrongAnswersUnresolved')}
          />
        )}
        <StatCard
          icon={<Bookmark size={20} aria-hidden="true" />}
          tone="accent"
          value={bookmarkCount}
          label={t('nav.bookmarks')}
        />
        {enabled.has('quiz_mcq') && (
          <StatCard
            icon={<ListChecks size={20} aria-hidden="true" />}
            tone="muted"
            value={pkg.mcqs.length}
            label="MCQs"
          />
        )}
        {/* Session 19AR · G7: only render when user has flagged anything —
            avoids cluttering a clean dashboard with a 0 widget. */}
        {flaggedCount > 0 && (
          <StatCard
            icon={<Flag size={20} aria-hidden="true" />}
            tone="warning"
            value={flaggedCount}
            label={t('dashboard.flaggedLabel')}
          />
        )}
      </div>

      {/* ── Cognitive level distribution + weakest top-3 (data analysis) ── */}
      {totalCogLabeled > 0 && (
        <div className="dashboard-analytics">
          <div className="dashboard-analytics-card">
            <h3 className="dashboard-analytics-title">
              <Compass size={14} aria-hidden="true" />
              {t('dashboard.cognitiveDistribution')}
            </h3>
            <p className="dashboard-analytics-help">{t('dashboard.cognitiveDistributionHelp')}</p>
            <CognitiveBar dist={cogDist} t={t} />
          </div>
          {/* 19BO+ B5.3 · 章×认知层级错题热图. 数据层 chapterCognitiveMatrix
              (stats.ts) 把 wrongAnswers ledger 按 caseId × cognitiveLevel 聚合。
              仅当存在未解决错题时渲染（避免空矩阵噪音）。与 cognitive
              distribution 互补：那条是"题目总量分布"，这条是"错题热度分布"。 */}
          {totalCogLabeled > 0 && cogMatrix.some((c) => c.unresolved > 0) && (
            <div className="dashboard-analytics-card">
              <h3 className="dashboard-analytics-title">
                <TrendingUp size={14} aria-hidden="true" />
                {t('dashboard.heatmapTitle')}
              </h3>
              <p className="dashboard-analytics-help">{t('dashboard.heatmapHelp')}</p>
              <ChapterCognitiveHeatmap matrix={cogMatrix} t={t} />
            </div>
          )}
          {/* B5 桥5: 后台自动诊断产出的待审 verdict 卡片.
              有 pending 时常驻顶部（优先级高于已 accept 的 diagnosis，因为
              它是"需要你处理的新结果"）。accept → 提升 diagnosis + 清 pending；
              reject → 清 pending。无 pending 时整块不渲染。 */}
          {progress.diagnosisPending && (
            <div
              className="dashboard-analytics-card diagnose-pending-card"
              data-testid="diagnosis-pending-card"
            >
              <h3 className="dashboard-analytics-title">
                <Sparkles size={14} aria-hidden="true" />
                {t('diagnose.pendingTitle')}
              </h3>
              <p className="dashboard-analytics-help">{t('diagnose.pendingHelp')}</p>
              <DiagnoseVerdictCard
                verdict={progress.diagnosisPending.verdict}
                statsSnapshot={progress.diagnosisPending.statsSnapshot}
              />
              <div className="diagnose-pending-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { void handleAcceptPending(); }}
                >
                  {t('diagnose.pendingAccept')}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { void handleRejectPending(); }}
                >
                  {t('diagnose.pendingReject')}
                </button>
              </div>
            </div>
          )}
          {/* 19BO+ Line 2 B4.3: AI 弱项诊断（course-dashboard 产品入口）.
              有 verdict 常驻展示 + 重新诊断；无 verdict 显示诊断 CTA。 */}
          <div className="dashboard-analytics-card">
            <h3 className="dashboard-analytics-title">
              <Sparkles size={14} aria-hidden="true" />
              {t('diagnose.title')}
            </h3>
            {progress.diagnosis ? (
              <>
                <DiagnoseVerdictCard
                  verdict={progress.diagnosis.verdict}
                  statsSnapshot={progress.diagnosis.statsSnapshot}
                  slot={
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setDiagnoseOpen(true)}
                    >
                      {t('dashboard.diagnoseRescoreBtn')}
                    </button>
                  }
                />
              </>
            ) : (
              <div className="diagnose-cta">
                <p className="dashboard-analytics-help">{t('diagnose.costHint')}</p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setDiagnoseOpen(true)}
                >
                  <Sparkles size={13} aria-hidden="true" /> {t('dashboard.diagnoseBtn')}
                </button>
              </div>
            )}
          </div>
          {weakChapters.length > 0 && (
            <div className="dashboard-analytics-card">
              <h3 className="dashboard-analytics-title">
                <AlertTriangle size={14} aria-hidden="true" />
                {t('dashboard.weakestChapters')}
              </h3>
              <ul className="weakest-list">
                {weakChapters.map((c) => (
                  <li key={c.chapterId}>
                    <Link to={`flashcards?case=${c.chapterId}&all=1`}>
                      <span className="weakest-name">{c.chapterName}</span>
                      <span className={`weakest-mastery ${masteryClass(c.mastery)}`}>
                        {c.mastery}%
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {weakTopics.length > 0 && (
            <div className="dashboard-analytics-card">
              <h3 className="dashboard-analytics-title">
                <Compass size={14} aria-hidden="true" />
                {t('dashboard.weakestTopics')}
              </h3>
              <ul className="weakest-list">
                {weakTopics.map((tp) => (
                  <li key={tp.topicId}>
                    <Link to={`quiz?topic=${encodeURIComponent(tp.topicId)}`}>
                      <span className="weakest-name">{tp.topicName}</span>
                      <span className="weakest-count">
                        {tp.unresolvedWrong} {t('dashboard.unresolvedShort')}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Content breakdown (v1-style: chapter / topic switch) ────────── */}
      <section className="dashboard-breakdown">
        <header className="dashboard-breakdown-head">
          <h2 className="dashboard-modules-heading">
            <BookOpen size={14} aria-hidden="true" />
            {viewMode === 'chapter' ? t('dashboard.chapterBreakdown') : t('dashboard.topicBreakdown')}
          </h2>
          <div className="dashboard-view-switch" role="tablist">
            <button
              role="tab"
              aria-selected={viewMode === 'chapter'}
              className={`dashboard-view-tab${viewMode === 'chapter' ? ' is-active' : ''}`}
              onClick={() => setViewMode('chapter')}
            >
              {t('dashboard.viewByChapter')}
            </button>
            <button
              role="tab"
              aria-selected={viewMode === 'topic'}
              className={`dashboard-view-tab${viewMode === 'topic' ? ' is-active' : ''}`}
              onClick={() => setViewMode('topic')}
            >
              {t('dashboard.viewByTopic')}
            </button>
          </div>
        </header>

        {viewMode === 'chapter' ? (
          <div className="chapter-grid">
            {chStats.map((c) => (
              <ChapterCard
                key={c.chapterId}
                stats={c}
                t={t}
                courseId={draft.id}
                enabledStages={enabledStages}
                onRegenComplete={() => { void refreshProgress(); }}
              />
            ))}
          </div>
        ) : tpStats.length === 0 ? (
          <StateCard
            variant="empty"
            icon={<Compass size={36} />}
            title={t('dashboard.noTopicsYet')}
          />
        ) : (
          <div className="topic-grid">
            {tpStats.map((tp) => (
              <TopicCard key={tp.topicId} stats={tp} chapters={chStats} t={t} />
            ))}
          </div>
        )}
      </section>

      {/* ── Recommended modules ────────────────────────────────────────── */}
      <h2 className="dashboard-modules-heading">
        <Sparkles size={14} aria-hidden="true" />
        {t('dashboard.recommendedNext')}
      </h2>

      <div className="dashboard-modules">
        {enabled.has('flashcards') && (
          <ModuleCard
            to="flashcards"
            icon={<Layers size={18} aria-hidden="true" />}
            label={pickLabel('flashcards', locale)}
            statValue={dueCount}
            statLabel={t('dashboard.flashcardsDue')}
            cta={t('dashboard.openModule')}
          />
        )}
        {enabled.has('quiz_mcq') && (
          <ModuleCard
            to="quiz"
            icon={<ListChecks size={18} aria-hidden="true" />}
            label={pickLabel('quiz_mcq', locale)}
            statValue={pkg.mcqs.length}
            statLabel="MCQs"
            cta={t('dashboard.openModule')}
          />
        )}
        <ModuleCard
          to="bookmarks"
          icon={<Bookmark size={18} aria-hidden="true" />}
          label={t('nav.bookmarks')}
          statValue={bookmarkCount}
          statLabel={t('bookmarks.heading')}
          cta={t('dashboard.openModule')}
        />
        {enabled.has('wrong_answers') && (
          <ModuleCard
            to="wrong-answers"
            icon={<AlertTriangle size={18} aria-hidden="true" />}
            label={pickLabel('wrong_answers', locale)}
            statValue={unresolvedCount}
            statLabel={t('dashboard.wrongAnswersUnresolved')}
            cta={t('dashboard.openModule')}
          />
        )}
        {import.meta.env.DEV && (
          <ModuleCard
            to="preview"
            icon={<Code2 size={18} aria-hidden="true" />}
            label={t('course.previewTab')}
            statValue={pkg.cases.length}
            statLabel="cases"
            cta={t('dashboard.openModule')}
          />
        )}
      </div>

      {progress.quizHistory.length === 0 && unresolvedCount === 0 && bookmarkCount === 0 && (
        <p className="dashboard-empty-hint">
          <TrendingUp size={14} aria-hidden="true" />
          {t('dashboard.noActivityYet')}
        </p>
      )}

      <DiagnoseDialog
        open={diagnoseOpen}
        onClose={() => setDiagnoseOpen(false)}
        stats={diagStats}
        onDiagnosed={handleDiagnosed}
      />
    </section>
  );
}
